import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchLegacyPacks,
  LegacyPackCode,
  LegacyPacksView,
} from "../../../api/legacyPacks";
import { I18nKey } from "../../../i18n/i18n";

/**
 * Packs d'une box legacy, en lecture seule : ils se modifient sur la machine, Copernic ne fait
 * que les afficher (aucun bouton d'ajout ni de retrait).
 */
export const legacyPackToI18nKey: Record<LegacyPackCode, I18nKey> = {
  // i18n.t("legacy-pack-energie-electrique")
  energie_electrique: "legacy-pack-energie-electrique",
  // i18n.t("legacy-pack-energie-gaz")
  energie_gaz: "legacy-pack-energie-gaz",
  // i18n.t("legacy-pack-besoin-auto")
  besoin_auto: "legacy-pack-besoin-auto",
  // i18n.t("legacy-pack-pesage-silos")
  pesage_silos: "legacy-pack-pesage-silos",
  // i18n.t("legacy-pack-dialogue-reseau")
  dialogue_reseau: "legacy-pack-dialogue-reseau",
  // i18n.t("legacy-pack-analyse-donnees")
  analyse_donnees: "legacy-pack-analyse-donnees",
  // i18n.t("legacy-pack-pesage-animaux")
  pesage_animaux: "legacy-pack-pesage-animaux",
};

const legacyPackToIcon: Record<LegacyPackCode, IconProp> = {
  energie_electrique: "bolt",
  energie_gaz: "fire-flame-simple",
  besoin_auto: "screwdriver-wrench",
  pesage_silos: "building",
  dialogue_reseau: "sync",
  analyse_donnees: "download",
  pesage_animaux: "crown",
};

export type LegacyPacksState = {
  view: LegacyPacksView | null;
  isLoading: boolean;
  error: string | null;
};

/** Packs d'un produit legacy, chargés une fois (`enabled` à faux pour les Serenity). */
export const useLegacyPacks = (
  uuid: string,
  enabled: boolean
): LegacyPacksState => {
  const [state, setState] = useState<LegacyPacksState>({
    view: null,
    isLoading: enabled,
    error: null,
  });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState((previous) => ({ ...previous, isLoading: true }));
    fetchLegacyPacks(uuid).then(({ view, error }) => {
      if (!cancelled) setState({ view, isLoading: false, error });
    });
    return () => {
      cancelled = true;
    };
  }, [uuid, enabled]);

  return state;
};

export const LegacyPacksSection = ({ state }: { state: LegacyPacksState }) => {
  const { t } = useTranslation();

  if (state.isLoading) return <div className="text-sm">…</div>;
  if (state.error || !state.view) {
    return <div className="text-sm">{t("legacy-packs-error")}</div>;
  }
  if (!state.view.known) {
    return <div className="text-xs">{t("legacy-packs-unknown")}</div>;
  }

  const { packs, temporary } = state.view;
  // Un pack temporaire (essai de 15 jours géré par la box) peut aussi être actif : il n'est
  // affiché qu'une fois, avec son badge.
  const permanent = packs.filter((code) => !temporary.includes(code));

  return (
    <div className="flex flex-1 flex-col gap-2">
      {permanent.length === 0 && temporary.length === 0 ? (
        <div className="text-sm">-</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {permanent.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-xs font-oxaniumMedium bg-grey-light text-grey-dark"
            >
              <FontAwesomeIcon icon={legacyPackToIcon[code]} />
              <span>{t(legacyPackToI18nKey[code])}</span>
            </span>
          ))}
          {temporary.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-xs font-oxaniumMedium bg-grey-light text-grey-dark"
            >
              <FontAwesomeIcon icon={legacyPackToIcon[code]} />
              <span>{t(legacyPackToI18nKey[code])}</span>
              <span className="px-1.5 rounded-full bg-secondary text-white">
                {t("legacy-packs-temporary")}
              </span>
            </span>
          ))}
        </div>
      )}
      {state.view.received_at && (
        <div className="text-xs text-grey-dark">
          {t("legacy-packs-received-at", {
            date: new Date(state.view.received_at).toLocaleString(),
          })}
        </div>
      )}
    </div>
  );
};
